import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import type { Resolver } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import Divider from "@mui/material/Divider";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Field from "@/components/FormInput/Field";
import tokens from "@/theme/tokens";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";

import emptyJudgebio from "@/images/empty_judge_bio.png";
import { updateJudgeBio } from "@/store/events-v1/eventActions";
import { fetchJudgeBio } from "@/store/profile/profileActions";
import {
  contentPanelSx,
  panelEmptyPromptSx,
  panelEmptySx,
  panelHeadSx,
} from "../styles";

interface JudgeBioProps {
  isUsersProfile: boolean;
}

type JudgeBioValues = { title: string; bio: string };

const judgeBioSchema = Yup.object({
  title: Yup.string().required("Title is required"),
  bio: Yup.string().default(""),
});

interface JudgeBioEditFormProps extends JudgeBioValues {
  isSavingJudgeBio: boolean;
  onSave: (values: JudgeBioValues) => void;
  onClose: () => void;
}

const JudgeBioEditForm = ({
  title,
  bio,
  isSavingJudgeBio,
  onSave,
  onClose,
}: JudgeBioEditFormProps) => {
  const { t } = useTranslation(["translation.Profile"]);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<JudgeBioValues>({
    defaultValues: { title: title || "", bio: bio || "" },
    // yup 0.32's schema types do not line up with @hookform/resolvers 5's
    // Resolver generic (title infers as `string | undefined`), so the cast
    // states what the schema actually guarantees at runtime.
    resolver: yupResolver(judgeBioSchema) as unknown as Resolver<JudgeBioValues>,
  });

  const submit = (values: JudgeBioValues) => {
    onSave(values);
    onClose();
  };

  // TextField's own ref goes to the root, so register()'s ref has to be handed
  // to the control it wraps.
  const { ref: titleRef, ...titleProps } = register("title");
  const { ref: bioRef, ...bioProps } = register("bio");

  return (
    <>
      <Box sx={contentPanelSx}>
        <Box sx={panelHeadSx}>
          <Typography variant="h2">{t("JudgeBio")}</Typography>
          <IconButton
            aria-label={t("JudgeBio")}
            onClick={onClose}
            size="small"
            sx={{ color: "grey.600" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box component="form" onSubmit={handleSubmit(submit)}>
          <Stack spacing={2}>
            <Field
              {...titleProps}
              inputRef={titleRef}
              fullWidth
              label={t("Title")}
              placeholder={t("Title")}
              error={Boolean(errors.title)}
              helperText={errors.title?.message}
              slotProps={{ htmlInput: { maxLength: 50 } }}
            />
            <TextField
              {...bioProps}
              inputRef={bioRef}
              multiline
              minRows={5}
              fullWidth
              placeholder={t("JudgeBioPhrase")}
            />
            <Button
              type="submit"
              variant="contained"
              size="small"
              disableElevation
              disabled={isSavingJudgeBio}
              startIcon={
                isSavingJudgeBio ? (
                  <CircularProgress size={14} color="inherit" />
                ) : null
              }
              sx={{ alignSelf: "flex-end" }}
            >
              {t("save")}
            </Button>
          </Stack>
        </Box>
      </Box>
      <Divider />
    </>
  );
};

const JudgeBio = ({ isUsersProfile }: JudgeBioProps) => {
  const { t } = useTranslation(["translation.Profile"]);
  const dispatch = useDispatch();
  const [isEditingJudgeBio, setIsEditingJudgeBio] = useState(false);

  const isJudge = useSelector((state: any) => {
    if (!isUsersProfile) {
      const judgeBio = state.browse.browseProfile.get("judgeBio");
      return judgeBio ? Boolean(judgeBio.get("bio")) : false;
    }
    const { userEventRoles } = state.hubs;
    const { userEventRoleTypes } = state.appData;
    return Boolean(
      userEventRoles.find(
        (uer: any) =>
          uer.get("user_id") === state.user.get("id") &&
          userEventRoleTypes
            .find(
              (uert: any) => uert.get("id") === uer.get("user_event_role_type_id")
            )
            .get("name") === "Judge"
      )
    );
  });

  const isLoaded = useSelector((state: any) =>
    isUsersProfile
      ? Boolean(state.profile.pageMetaData.get("judgeBioLoaded"))
      : true
  );
  const bio = useSelector((state: any) =>
    isUsersProfile
      ? state.profile.judgeBio.get("bio")
      : state.browse.browseProfile.get("judgeBio")?.get("bio") ?? null
  );
  const title = useSelector((state: any) =>
    isUsersProfile
      ? state.profile.judgeBio.get("title")
      : state.browse.browseProfile.get("judgeBio")?.get("title") ?? null
  );
  // NB: the class this replaced also read isSavingSummary here, not a judge
  // bio flag of its own. Kept as-is so the spinner behaves the same.
  const isSavingJudgeBio = useSelector((state: any) =>
    isUsersProfile
      ? Boolean(state.profile.pageMetaData.get("isSavingSummary"))
      : false
  );

  const getJudgeBio = useCallback(() => dispatch(fetchJudgeBio()), [dispatch]);

  useEffect(() => {
    if (isUsersProfile && isJudge) getJudgeBio();
  }, [isUsersProfile, isJudge, getJudgeBio]);

  if (!isJudge) return null;

  if (!isLoaded) {
    return (
      <Box sx={contentPanelSx}>
        <Box sx={panelHeadSx}>
          <Typography variant="h2">{t("JudgeBio")}</Typography>
        </Box>
        <Typography variant="body2" sx={{ color: "grey.800" }}>
          {t("loading")}
        </Typography>
      </Box>
    );
  }

  if (isEditingJudgeBio) {
    return (
      <JudgeBioEditForm
        title={title}
        bio={bio}
        isSavingJudgeBio={isSavingJudgeBio}
        onSave={(values) =>
          dispatch(updateJudgeBio(values.title, values.bio, getJudgeBio))
        }
        onClose={() => setIsEditingJudgeBio(false)}
      />
    );
  }

  const editButton = (
    <IconButton
      aria-label={t("JudgeBio")}
      onClick={() => setIsEditingJudgeBio(true)}
      size="small"
      sx={{ color: tokens.color.accentTeal }}
    >
      <EditOutlinedIcon fontSize="small" />
    </IconButton>
  );

  if (bio || title) {
    return (
      <>
        <Box sx={contentPanelSx}>
          <Box sx={panelHeadSx}>
            <Typography variant="h2">{t("JudgeBio")}</Typography>
            {isUsersProfile && editButton}
          </Box>
          <Stack spacing={1}>
            <Typography variant="h3">{title}</Typography>
            <Typography variant="body1">{bio}</Typography>
          </Stack>
        </Box>
        <Divider />
      </>
    );
  }

  const isOwner = isUsersProfile;
  return (
    <>
      <Box sx={contentPanelSx}>
        <Box sx={panelHeadSx}>
          <Typography variant="h2">{t("JudgeBio")}</Typography>
          {isOwner && editButton}
        </Box>
        <Box sx={panelEmptySx}>
          <Box
            component="img"
            src={emptyJudgebio}
            alt={isOwner ? "empty bio prompt" : "empty character waving hi"}
          />
          <Stack spacing={2} sx={{ ...panelEmptyPromptSx, alignItems: "flex-start" }}>
            <Typography variant="body1" sx={{ color: "grey.800" }}>
              {isOwner ? t("whoAreYou") : t("NoJudgeBio")}
            </Typography>
            {isOwner && (
              <Button
                onClick={() => setIsEditingJudgeBio(true)}
                variant="outlined"
                size="small"
              >
                {t("EditBio")}
              </Button>
            )}
          </Stack>
        </Box>
      </Box>
      <Divider />
    </>
  );
};

export default JudgeBio;
